/**
 * @file Multi-tenant provider management system
 * @module multi-tenant-provider
 *
 * @remarks
 * This module provides a centralized manager for user-specific providers
 * in a multi-tenant environment. It ensures proper isolation between users
 * while efficiently sharing common resources like app configuration.
 */

import { Providers } from "./manager.js";
import { ConfigProvider } from "./config/provider.js";
import { newSecretStorage } from "./config/secrets.js";
import {
  storedConfigSchema,
  AppConfig,
  Config,
  CredentialsConfig,
} from "./config/schemas.js";
import { AuthProvider } from "./auth/provider.js";
import { DummyAnalyticsProvider } from "./analytics/provider.js";
import { logger } from "./logger.js";
import { IAuthStore } from "./auth/auth-store.js";
import { PropelAuthInfo } from "./auth/propel.js";
import { apiKeyFromConfig } from "./api.js";

/**
 * Options for configuring the multi-tenant provider manager
 */
export interface MultiTenantProviderOptions {
  /**
   * Maximum number of concurrent user providers to maintain
   * If exceeded, least recently used providers are evicted
   */
  maxUsers?: number;

  /**
   * Time-to-live for idle providers in milliseconds
   * Providers not accessed within this time are eligible for cleanup
   */
  providerTTL?: number;

  /**
   * Interval for running cleanup operations in milliseconds
   * Default: 5 minutes
   */
  cleanupInterval?: number;

  /**
   * Base API configuration to use for all users
   * If not provided, uses app's demo sandbox config
   */
  baseApiConfig?: Config["api"];
}

/**
 * Interface for multi-tenant provider management
 */
export interface IMultiTenantProviderManager {
  /**
   * Get or create providers for a specific user
   * @param userId - The unique identifier for the user
   * @returns Complete set of providers for the user
   */
  getProviders(userId: string): Promise<Providers>;

  /**
   * Update authentication information for a user
   * @param userId - The unique identifier for the user
   * @param authInfo - The authentication information to update
   */
  updateAuth(userId: string, authInfo: PropelAuthInfo): Promise<void>;

  /**
   * Remove all providers for a specific user
   * @param userId - The unique identifier for the user
   */
  removeUser(userId: string): void;

  /**
   * Check if a user has active providers
   * @param userId - The unique identifier for the user
   * @returns true if the user has providers
   */
  hasUser(userId: string): boolean;

  /**
   * Get all active user IDs
   * @returns Array of user IDs with active providers
   */
  getAllUsers(): string[];

  /**
   * Clean up expired providers
   */
  cleanup(): void;

  /**
   * Destroy the manager and clean up all resources
   */
  destroy(): void;
}

/**
 * Provider cache entry with metadata
 */
interface ProviderCacheEntry {
  providers: Providers;
  createdAt: Date;
  lastAccessed: Date;
}

/**
 * Multi-tenant provider manager implementation
 *
 * @remarks
 * This class manages provider instances for multiple users in a server environment.
 * It ensures each user has isolated configuration and credentials while sharing
 * common resources like application configuration.
 *
 * @example
 * ```typescript
 * const authStore = createAuthStore<PropelAuthInfo>({ ttl: 3600000 });
 * const manager = new MultiTenantProviderManager(appConfig, authStore);
 *
 * // Get providers for a user
 * const providers = await manager.getProviders("user123");
 *
 * // Update user's auth
 * await manager.updateAuth("user123", newAuthInfo);
 * ```
 */
export class MultiTenantProviderManager implements IMultiTenantProviderManager {
  private readonly appConfig: AppConfig;
  private readonly authStore: IAuthStore<PropelAuthInfo>;
  private readonly providersCache = new Map<string, ProviderCacheEntry>();
  private readonly options: Required<MultiTenantProviderOptions>;
  private cleanupTimer?: NodeJS.Timeout;

  constructor(
    appConfig: AppConfig,
    authStore: IAuthStore<PropelAuthInfo>,
    options: MultiTenantProviderOptions = {},
  ) {
    this.appConfig = appConfig;
    this.authStore = authStore;
    this.options = {
      maxUsers: options.maxUsers ?? 1000,
      providerTTL: options.providerTTL ?? 3600000, // 1 hour default
      cleanupInterval: options.cleanupInterval ?? 300000, // 5 minutes default
      baseApiConfig: options.baseApiConfig ?? appConfig.demoSandbox.api,
    };

    // Start cleanup timer if TTL is configured
    if (this.options.providerTTL > 0) {
      this.startCleanupTimer();
    }

    logger.info("MultiTenantProviderManager initialized", {
      maxUsers: this.options.maxUsers,
      providerTTL: this.options.providerTTL,
      cleanupInterval: this.options.cleanupInterval,
    });
  }

  /**
   * Get or create providers for a specific user
   */
  async getProviders(userId: string): Promise<Providers> {
    // Check cache first
    const cached = this.providersCache.get(userId);
    if (cached) {
      cached.lastAccessed = new Date();
      logger.debug("Returning cached providers", { userId });
      return cached.providers;
    }

    // Get auth info from store
    const authInfo = this.authStore.get(userId);
    if (!authInfo) {
      throw new Error(`No authentication found for user: ${userId}`);
    }

    // Check cache size limit
    if (this.providersCache.size >= this.options.maxUsers) {
      this.evictOldestProvider();
    }

    // Create new providers for the user
    const providers = await this.createUserProviders(userId, authInfo);

    // Cache the providers
    const entry: ProviderCacheEntry = {
      providers,
      createdAt: new Date(),
      lastAccessed: new Date(),
    };
    this.providersCache.set(userId, entry);

    logger.info("Created new providers for user", { userId });
    return providers;
  }

  /**
   * Update authentication information for a user
   */
  async updateAuth(userId: string, authInfo: PropelAuthInfo): Promise<void> {
    // Update auth in store
    this.authStore.set(userId, authInfo);

    // Update existing providers if cached
    const cached = this.providersCache.get(userId);
    if (cached) {
      const { providers } = cached;

      // Extract credentials from auth info
      const credentials = this.extractCredentials(authInfo);

      // Update the config provider with new credentials
      await providers.configProvider.update({
        credentials,
        identity: {
          user: {
            id: userId,
            email: authInfo.extra?.email || `${userId}@toolprint.local`,
          },
        },
      });

      cached.lastAccessed = new Date();
      logger.debug("Updated auth for existing providers", { userId });
    }
  }

  /**
   * Remove all providers for a specific user
   */
  removeUser(userId: string): void {
    // Remove from auth store
    this.authStore.remove(userId);

    // Remove from cache
    if (this.providersCache.delete(userId)) {
      logger.info("Removed providers for user", { userId });
    }
  }

  /**
   * Check if a user has active providers
   */
  hasUser(userId: string): boolean {
    return this.providersCache.has(userId) || this.authStore.has(userId);
  }

  /**
   * Get all active user IDs
   */
  getAllUsers(): string[] {
    // Combine users from cache and auth store
    const users = new Set<string>();

    // Add cached users
    for (const userId of this.providersCache.keys()) {
      users.add(userId);
    }

    // Add users with auth but no cached providers
    for (const userId of this.authStore.keys()) {
      users.add(userId);
    }

    return Array.from(users);
  }

  /**
   * Clean up expired providers
   */
  cleanup(): void {
    const now = new Date();
    const ttl = this.options.providerTTL;
    let removed = 0;

    for (const [userId, entry] of this.providersCache) {
      const age = now.getTime() - entry.lastAccessed.getTime();
      if (age > ttl) {
        this.providersCache.delete(userId);
        removed++;
      }
    }

    if (removed > 0) {
      logger.info(`Cleaned up ${removed} expired provider entries`);
    }
  }

  /**
   * Destroy the manager and clean up all resources
   */
  destroy(): void {
    // Stop cleanup timer
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }

    // Clear all caches
    this.providersCache.clear();

    logger.info("MultiTenantProviderManager destroyed");
  }

  /**
   * Create providers for a specific user
   */
  private async createUserProviders(
    userId: string,
    authInfo: PropelAuthInfo,
  ): Promise<Providers> {
    // Create in-memory secret storage
    const secretStorage = await newSecretStorage(
      storedConfigSchema,
      "in-memory",
    );

    // Create config provider with in-memory storage
    const configProvider = new ConfigProvider(this.appConfig, secretStorage);

    // Initialize the config provider first to set up defaults
    await configProvider.init();

    // Extract credentials from auth info
    let credentials = this.extractCredentials(authInfo);

    // First, set up the initial config with credentials
    await configProvider.update({
      api: this.options.baseApiConfig,
      credentials,
      identity: {
        user: {
          id: userId,
          email: authInfo.extra?.email || `${userId}@toolprint.local`,
        },
      },
    });

    // If OAuth user (no API key), fetch the API key using the access token
    if (!authInfo.extra?.apiKey && authInfo.extra?.propelAccessToken) {
      try {
        // Fetch API key using the OAuth token
        const config = await configProvider.getConfig();
        const apiKey = await apiKeyFromConfig(config);

        // Update credentials to include the API key
        credentials = {
          accessToken: String(authInfo.extra.propelAccessToken),
          apiKey: apiKey,
        };

        // Update the config with the API key
        await configProvider.update({ credentials });

        // Update auth info with the fetched API key for future use
        authInfo.extra.apiKey = apiKey;
        this.authStore.set(userId, authInfo);

        logger.info("Fetched and stored API key for OAuth user", { userId });
      } catch (error) {
        logger.error("Failed to fetch API key for OAuth user", {
          userId,
          error: error instanceof Error ? error.message : String(error),
        });
        // Continue without API key - will use OAuth token
      }
    }

    // Config has already been updated above

    // Create auth provider with the user-specific config
    const authProvider = new AuthProvider({ configProvider });

    // Create analytics provider (using dummy for user sessions)
    const analyticsProvider = new DummyAnalyticsProvider({
      appConfig: this.appConfig,
      configProvider,
    });

    // Create a minimal flags provider for user sessions
    const flagsProvider = {
      validateFlagName: (flagName: string) => {
        // No-op for user sessions
      },
      all: async () => ({}),
      value: async (flagName: string) => undefined,
    } as any; // Type assertion needed due to private class

    return {
      configProvider,
      authProvider,
      analyticsProvider,
      flagsProvider,
    };
  }

  /**
   * Extract credentials from PropelAuthInfo
   */
  private extractCredentials(authInfo: PropelAuthInfo): CredentialsConfig {
    // Check if this is API key auth
    if (authInfo.extra?.apiKey) {
      return {
        accessToken: "", // Empty for API key auth
        apiKey: String(authInfo.extra.apiKey),
      };
    }

    // OAuth credentials
    return {
      accessToken: String(authInfo.extra?.propelAccessToken || ""),
    };
  }

  /**
   * Evict the oldest provider based on last accessed time
   */
  private evictOldestProvider(): void {
    let oldestUserId: string | null = null;
    let oldestTime = new Date();

    for (const [userId, entry] of this.providersCache) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestUserId = userId;
      }
    }

    if (oldestUserId) {
      this.providersCache.delete(oldestUserId);
      logger.debug(`Evicted oldest provider entry: ${oldestUserId}`);
    }
  }

  /**
   * Start the cleanup timer
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.options.cleanupInterval);
  }
}

/**
 * Create a multi-tenant provider manager with default options
 */
export function createMultiTenantProviderManager(
  appConfig: AppConfig,
  authStore: IAuthStore<PropelAuthInfo>,
  options?: MultiTenantProviderOptions,
): IMultiTenantProviderManager {
  return new MultiTenantProviderManager(appConfig, authStore, options);
}
